import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Trophy, Medal, Star, TrendingUp, Users, Target, Award,
  Crown, Flame, Sparkles, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Leaderboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [productivityData, setProductivityData] = useState([]);
  const [capacityData, setCapacityData] = useState([]);
  const [teamStats, setTeamStats] = useState({});
  
  const fetchData = async () => {
    try {
      const [prodRes, capRes] = await Promise.all([
        fetch(`${API_URL}/api/productivity/clinicians`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/capacity/rncm`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (prodRes.ok) {
        const data = await prodRes.json();
        setProductivityData(data);
      }
      if (capRes.ok) {
        const data = await capRes.json();
        setCapacityData(data);
        
        // Calculate team stats
        const teams = {};
        data.forEach(c => {
          const team = c.team || 'Unknown';
          if (!teams[team]) {
            teams[team] = { members: 0, totalCapacity: 0, atCapacity: 0 };
          }
          teams[team].members += 1;
          teams[team].totalCapacity += c.visit_capacity || 0;
          if (c.planning_outcome === 'At Capacity') teams[team].atCapacity += 1;
        });
        setTeamStats(teams);
      }
    } catch (error) {
      toast.error('Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [token]);
  
  // Calculate rankings
  const getProductivityRankings = () => {
    return [...productivityData]
      .filter(p => p.total_visits > 0)
      .sort((a, b) => {
        // Score based on productivity rate and total visits
        const scoreA = (a.productivity_rate || 0) * 0.7 + Math.min(a.total_visits / 100, 30) * 0.3;
        const scoreB = (b.productivity_rate || 0) * 0.7 + Math.min(b.total_visits / 100, 30) * 0.3;
        return scoreB - scoreA;
      })
      .slice(0, 10);
  };
  
  const getCapacityRankings = () => {
    return [...capacityData]
      .filter(c => c.planning_outcome === 'At Capacity')
      .sort((a, b) => (b.visit_capacity || 0) - (a.visit_capacity || 0))
      .slice(0, 10);
  };
  
  const getTeamRankings = () => {
    return Object.entries(teamStats)
      .map(([name, stats]) => ({
        name,
        ...stats,
        efficiency: stats.members > 0 ? (stats.atCapacity / stats.members * 100).toFixed(0) : 0
      }))
      .sort((a, b) => b.efficiency - a.efficiency);
  };
  
  const getRankBadge = (rank) => {
    if (rank === 0) return <Crown className="h-5 w-5 text-yellow-500" />;
    if (rank === 1) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-medium text-muted-foreground">#{rank + 1}</span>;
  };
  
  const getRankStyle = (rank) => {
    if (rank === 0) return "bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200";
    if (rank === 1) return "bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20 border-gray-200";
    if (rank === 2) return "bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200";
    return "";
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  const productivityRankings = getProductivityRankings();
  const capacityRankings = getCapacityRankings();
  const teamRankings = getTeamRankings();
  
  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2">
            <Trophy className="h-7 w-7 text-yellow-500" />
            Team Leaderboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Recognize top performers and track team achievements
          </p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/10 dark:to-amber-900/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <Crown className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Top Performer</p>
                <p className="font-semibold truncate text-sm">
                  {productivityRankings[0]?.clinician_name || 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <Users className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Staff</p>
                <p className="text-xl font-bold">{capacityData.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <Target className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">At Capacity</p>
                <p className="text-xl font-bold">
                  {capacityData.filter(c => c.planning_outcome === 'At Capacity').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Flame className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Productivity</p>
                <p className="text-xl font-bold">
                  {productivityData.length > 0 
                    ? (productivityData.reduce((sum, p) => sum + (p.productivity_rate || 0), 0) / productivityData.length).toFixed(0)
                    : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="productivity" className="space-y-4">
        <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-3 h-auto">
          <TabsTrigger value="productivity" className="gap-2">
            <TrendingUp className="h-4 w-4" /> Productivity
          </TabsTrigger>
          <TabsTrigger value="capacity" className="gap-2">
            <Target className="h-4 w-4" /> Capacity
          </TabsTrigger>
          <TabsTrigger value="teams" className="gap-2">
            <Users className="h-4 w-4" /> Teams
          </TabsTrigger>
        </TabsList>
        
        {/* Productivity Tab */}
        <TabsContent value="productivity">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-teal-500" />
                Top Producers
              </CardTitle>
              <CardDescription>Clinicians ranked by productivity rate and visit volume</CardDescription>
            </CardHeader>
            <CardContent>
              {productivityRankings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No productivity data available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {productivityRankings.map((person, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center gap-4 p-4 rounded-lg border ${getRankStyle(idx)}`}
                    >
                      <div className="flex-shrink-0">
                        {getRankBadge(idx)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{person.clinician_name}</p>
                        <p className="text-sm text-muted-foreground">{person.team || 'Unknown Team'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-teal-600">{person.productivity_rate?.toFixed(0) || 0}%</p>
                        <p className="text-xs text-muted-foreground">{person.total_visits} visits</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Capacity Tab */}
        <TabsContent value="capacity">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-emerald-500" />
                Optimal Capacity Stars
              </CardTitle>
              <CardDescription>Clinicians maintaining optimal workload balance</CardDescription>
            </CardHeader>
            <CardContent>
              {capacityRankings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No capacity data available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {capacityRankings.map((person, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center gap-4 p-4 rounded-lg border ${getRankStyle(idx)}`}
                    >
                      <div className="flex-shrink-0">
                        {getRankBadge(idx)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{person.clinician_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                            {person.planning_outcome}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{person.team || 'Unknown Team'}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-emerald-600">{person.visit_capacity}</p>
                        <p className="text-xs text-muted-foreground">visits/week</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Teams Tab */}
        <TabsContent value="teams">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-purple-500" />
                Team Performance
              </CardTitle>
              <CardDescription>Teams ranked by efficiency and capacity balance</CardDescription>
            </CardHeader>
            <CardContent>
              {teamRankings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No team data available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {teamRankings.map((team, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border ${getRankStyle(idx)}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {getRankBadge(idx)}
                          <div>
                            <p className="font-semibold">{team.name}</p>
                            <p className="text-xs text-muted-foreground">{team.members} members</p>
                          </div>
                        </div>
                        <Badge className={
                          team.efficiency >= 80 ? "bg-emerald-500" :
                          team.efficiency >= 50 ? "bg-amber-500" : "bg-red-500"
                        }>
                          {team.efficiency}% Efficient
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Team Efficiency</span>
                          <span className="font-medium">{team.atCapacity}/{team.members} at capacity</span>
                        </div>
                        <Progress value={parseInt(team.efficiency)} className="h-2" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
